/**
 * MetricsService - Сервис для получения метрик
 * =============================================
 * 
 * Централизованный доступ ко всем метрикам через новый универсальный API
 */

import { ApiService } from './api';
import { FiltersService } from './filtersService';
import { swapNetFields } from '../config/platformFees';
import { IS_DEBUG } from '../utils/debug';

/**
 * Контракт options всех фетчеров сервиса:
 *   { signal?: AbortSignal, force?: boolean }
 * signal — отмена запроса (useWidgetData отдаёт свой при смене фильтров/размонтировании),
 * force  — игнорировать клиентский TTL-кэш (кнопка Retry в BaseWidget).
 */

/**
 * Время жизни клиентского кэша ответов.
 * 5 минут: этого хватает, чтобы переключение вкладок раздела «Пользователи»
 * (Регистрации/DAU-MAU/Доходы/ARPDAU), которое размонтирует и заново монтирует
 * виджет, било в кэш, а не гоняло тот же запрос повторно. Кэш всё ещё короче
 * серверного TTL (300s, 600s у LTV) и не маскирует ручной сброс серверного
 * кэша — после clear-cache и перезагрузки секции запрос уходит на сервер,
 * а не отдаётся из браузера (это решается server-side инвалидацией, не тут).
 * Дедуп одновременных запросов от этого окна не зависит: он живёт в inflight.
 */
const CLIENT_CACHE_TTL_MS = 5 * 60 * 1000;

export class MetricsService {
  /**
   * Получить данные кампаний
   * Без клиентского кэша: потребитель сортирует массив на месте.
   */
  static async getCampaigns(filters, options = {}) {
    const params = FiltersService.toQueryParams(filters);
    return ApiService.get('/campaigns', params, { signal: options.signal });
  }

  /**
   * Получить данные креативов
   * Без клиентского кэша: крупный payload, потребитель мутирует его на месте.
   */
  static async getCreatives(filters, options = {}) {
    const params = FiltersService.toQueryParams(filters);
    return ApiService.get('/creatives', params, { signal: options.signal });
  }

  /**
   * Получить регистрации
   */
  static async getRegistrations(filters, groupBy = 'day', options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      groupBy
    };
    return this.cachedGet('/registrations', params, options);
  }

  /**
   * Получить метрику по типу
   */
  static async getMetric(metricType, filters, options = {}) {
    const params = FiltersService.toQueryParams(filters);
    return this.cachedGet(`/metrics/${metricType}`, params, options);
  }

  /**
   * Получить установки
   */
  static async getInstalls(filters, options = {}) {
    return this.getMetric('installs', filters, options);
  }

  /**
   * Получить установки по периодам
   */
  static async getInstallsByPeriod(filters, options = {}) {
    return this.getMetric('installs-by-period', filters, options);
  }

  /**
   * Получить установки по кампаниям
   */
  static async getInstallsByCampaign(filters, options = {}) {
    return this.getMetric('installs-by-campaign', filters, options);
  }

  /**
   * Получить затраты
   */
  static async getCosts(filters, options = {}) {
    return this.getMetric('costs', filters, options);
  }

  /**
   * Получить затраты по периодам
   */
  static async getCostsByPeriod(filters, options = {}) {
    return this.getMetric('costs-by-period', filters, options);
  }

  /**
   * Получить затраты по кампаниям
   */
  static async getCostsByCampaign(filters, options = {}) {
    return this.getMetric('costs-by-campaign', filters, options);
  }

  /**
   * Получить метрики затрат (CPI и т.д.)
   */
  static async getCostMetrics(filters, options = {}) {
    return this.getMetric('cost-metrics', filters, options);
  }

  /**
   * Получить доходы
   */
  static async getRevenue(filters, options = {}) {
    return this.getMetric('revenue', filters, options);
  }

  /**
   * Получить доходы по периодам
   */
  static async getRevenueByPeriod(filters, groupBy = 'day', options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      groupBy
    };
    return this.cachedGet('/metrics/revenue-by-period', params, options);
  }

  /**
   * Получить доходы по кампаниям
   */
  static async getRevenueByCampaign(filters, options = {}) {
    return this.getMetric('revenue-by-campaign', filters, options);
  }

  /**
   * Получить список "китов" — игроков с суммарным доходом выше minRevenue.
   * Платформа/проект/кампании на лидерборд не влияют, а вот глобальный
   * диапазон дат сужает окно подсчёта дохода (без него сервер читает всю
   * историю: 9.3 s и 22.6 GiB на вызов).
   * @param {number} minRevenue порог суммарного дохода
   * @param {Object|null} filters фильтры дашборда (используется только dateRange)
   */
  static async getWhales(minRevenue = 1000, filters = null, options = {}) {
    const { startDate, endDate } = filters ? FiltersService.toQueryParams(filters) : {};
    const params = { minRevenue };
    if (startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    }
    const response = await this.cachedGet('/users/whales', params, options);
    return response || [];
  }

  /**
   * Получить когортную таблицу удержания (D1/D3/D7/D14/D30) по дате регистрации
   */
  static async getRetentionCohort(filters, options = {}) {
    return this.cachedGet('/retention', FiltersService.toQueryParams(filters), options);
  }

  /**
   * Получить кривую удержания (% когорты, активной на day-й день после регистрации)
   */
  static async getRetentionCurve(filters, maxDays = 30, options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      maxDays
    };
    return this.cachedGet('/retention/curve', params, options);
  }

  /**
   * Получить ретеншен N-го дня по юнитам регистрации (день/неделя/месяц)
   * @param {Object} filters - Глобальные фильтры дашборда
   * @param {string} granularity - 'day' | 'week' | 'month'
   * @param {number[]} days - Дни N (R1, R7, R30, ...)
   */
  static async getRetentionByPeriod(filters, granularity = 'day', days = [1, 7, 30], options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      granularity,
      days: days.join(',')
    };
    return this.cachedGet('/retention/by-period', params, options);
  }

  /**
   * Получить список стран окна регистраций для селектора гео в «Ретеншене».
   * Гео из filters здесь намеренно не передаётся: список должен оставаться
   * полным, иначе выбранная страна выкинула бы из селектора все остальные.
   * @returns {Promise<Array<{ geo: string, cohortSize: number }>>}
   */
  static async getRetentionGeoOptions(filters, options = {}) {
    const { geo, ...withoutGeo } = filters || {};
    const response = await this.cachedGet(
      '/retention/geo-options',
      FiltersService.toQueryParams(withoutGeo),
      options
    );
    return response || [];
  }

  /**
   * Получить сводку по всем метрикам
   */
  static async getSummary(filters, options = {}) {
    const params = FiltersService.toQueryParams(filters);
    return this.cachedGet('/metrics/summary', params, options);
  }

  /**
   * Получить несколько метрик одним запросом
   */
  static async getBatchMetrics(metricTypes, filters, options = {}) {
    const body = {
      metrics: metricTypes,
      filters: FiltersService.toQueryParams(filters)
    };
    return ApiService.post('/metrics/batch', body, { signal: options.signal });
  }

  /**
   * Получить список доступных метрик
   */
  static async getAvailableMetrics(options = {}) {
    return ApiService.get('/metrics/available', {}, { signal: options.signal });
  }

  /**
   * Получить список доступных фильтров
   */
  static async getAvailableFilters(options = {}) {
    return ApiService.get('/filters/available', {}, { signal: options.signal });
  }

  /**
   * Получить значения для фильтра
   */
  static async getFilterValues(filterType, filters = {}, options = {}) {
    const params = FiltersService.toQueryParams(filters);
    return ApiService.get(`/filters/${filterType}`, params, { signal: options.signal });
  }

  /**
   * Кэширование результатов
   * cache    — короткоживущий TTL-кэш ответов, ограничен maxCacheEntries
   *            (без бонда рос бы по числу уникальных комбинаций фильтров)
   * inflight — дедуп одновременных одинаковых запросов; именно он решает
   *            задачу A6 (два виджета монтируются раньше, чем придёт ответ),
   *            TTL-кэш такие случаи не ловит в принципе
   */
  static cache = new Map();
  static inflight = new Map();
  static cacheTimeout = CLIENT_CACHE_TTL_MS;
  static maxCacheEntries = 100;

  /**
   * GET с TTL-кэшем, дедупом и отменой
   * Ключ строится из ТЕХ ЖЕ нормализованных параметров, из которых собирается
   * URL — иначе { platform: undefined } и отсутствующий platform дали бы разные
   * ключи при одинаковом запросе и дедуп бы промахнулся.
   * @param {string} endpoint - Путь API
   * @param {Object} params - Query-параметры
   * @param {Object} options - { signal, force }
   * @returns {Promise<*>}
   */
  static cachedGet(endpoint, params = {}, options = {}) {
    const key = ApiService.buildUrl(endpoint, params);

    return this.getCached(
      key,
      (signal) => ApiService.get(endpoint, params, { signal }),
      options
    );
  }

  /**
   * Получить данные с кэшированием и дедупом одновременных запросов
   * @param {string} key - Ключ кэша
   * @param {(signal: AbortSignal) => Promise<*>} fetchFn - Загрузчик, получает общий signal
   * @param {Object} options - { signal, force }
   * @returns {Promise<*>}
   */
  static getCached(key, fetchFn, options = {}) {
    const { signal, force = false } = options;

    if (!force) {
      const cached = this.cache.get(key);
      if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
        if (IS_DEBUG) console.log(`Cache hit: ${key}`);
        return Promise.resolve(cached.data);
      }
    }

    let entry = force ? null : this.inflight.get(key);

    if (!entry) {
      // Общий AbortController запроса: прерываем его только когда от него
      // отписались ВСЕ ожидающие (иначе отмена одного виджета уронит второй).
      const controller = new AbortController();
      entry = { key, controller, refs: 0, promise: null };
      entry.promise = (async () => {
        try {
          const data = await fetchFn(controller.signal);
          this.setCached(key, data);
          return data;
        } finally {
          if (this.inflight.get(key) === entry) {
            this.inflight.delete(key);
          }
        }
      })();
      if (!force) {
        this.inflight.set(key, entry);
      }
      if (IS_DEBUG) console.log(`Cache miss: ${key}`);
    } else if (IS_DEBUG) {
      console.log(`Inflight join: ${key}`);
    }

    entry.refs += 1;
    return this.joinInflight(entry, signal);
  }

  /**
   * Подписка вызывающего на общий in-flight запрос
   * @param {Object} entry - Запись in-flight запроса
   * @param {AbortSignal} [signal] - Signal вызывающего
   * @returns {Promise<*>}
   */
  static joinInflight(entry, signal) {
    return new Promise((resolve, reject) => {
      let released = false;

      const release = () => {
        if (released) return;
        released = true;
        if (signal) signal.removeEventListener('abort', onAbort);
        entry.refs -= 1;
        if (entry.refs <= 0) {
          if (this.inflight.get(entry.key) === entry) {
            this.inflight.delete(entry.key);
          }
          // no-op, если запрос уже завершился
          entry.controller.abort();
        }
      };

      function onAbort() {
        release();
        reject(new DOMException('Aborted', 'AbortError'));
      }

      // Подписываемся на общий промис ДО обработки уже отменённого signal:
      // иначе единственный (и он же создавший запрос) вызывающий ушёл бы
      // раньше, чем у промиса появился потребитель, и его AbortError всплыл бы
      // как unhandled rejection.
      entry.promise.then(
        (data) => {
          if (released) return;
          release();
          resolve(data);
        },
        (error) => {
          if (released) return;
          release();
          reject(error);
        }
      );

      if (signal) {
        if (signal.aborted) {
          onAbort();
          return;
        }
        signal.addEventListener('abort', onAbort);
      }
    });
  }

  /**
   * Положить значение в кэш, удерживая размер в пределах maxCacheEntries
   * @param {string} key - Ключ кэша
   * @param {*} data - Данные ответа
   * @returns {void}
   */
  static setCached(key, data) {
    // Переустановка ключа двигает его в конец Map = свежий хвост (LRU-порядок)
    this.cache.delete(key);
    this.cache.set(key, { data, timestamp: Date.now() });

    if (this.cache.size <= this.maxCacheEntries) return;

    // Сначала выселяем протухшее, затем — самое старое по порядку вставки
    const now = Date.now();
    for (const [cachedKey, entry] of this.cache) {
      if (this.cache.size <= this.maxCacheEntries) break;
      if (now - entry.timestamp >= this.cacheTimeout) {
        this.cache.delete(cachedKey);
      }
    }

    while (this.cache.size > this.maxCacheEntries) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Очистить кэш
   * Заодно прерываем незавершённые запросы: иначе ответ, вылетевший до очистки,
   * положил бы устаревшие данные обратно в кэш уже после неё.
   */
  static clearCache() {
    this.cache.clear();
    for (const entry of this.inflight.values()) {
      entry.controller.abort();
    }
    this.inflight.clear();
  }

  /**
   * Получить доходы AdMon по периодам с разбивкой по платформам
   */
  static async getAdMonByPeriod(filters, groupBy = 'day', options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      groupBy
    };
    return this.cachedGet('/metrics/admon-by-period', params, options);
  }

  /**
   * Получить доходы InApps по периодам с разбивкой по платформам
   */
  static async getInAppsByPeriod(filters, groupBy = 'day', options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      groupBy
    };
    return this.cachedGet('/metrics/inapps-by-period', params, options);
  }

  /**
   * Получить данные LTV (Lifetime Value) для когортного анализа
   */
  static async getLtvData(filters, options = {}) {
    return this.getMetric('ltv', filters, options);
  }
  
  /**
   * Получить данные LTV по периодам с разбивкой по источникам
   * @param {Object} filters - Фильтры дашборда
   * @param {string} groupBy - Группировка (day/week/month)
   * @param {number} dayFromInstall - День от установки (0, 1, 3, 7, 14, 30, 60, 90, 120, 150, 180, 360)
   */
  static async getLtvByPeriod(filters, groupBy = 'day', dayFromInstall = 7, options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      groupBy,
      dayFromInstall: dayFromInstall.toString() // Явно передаем как query параметр
    };
    return this.cachedGet('/metrics/ltv-by-period', params, options);
  }
  
  /**
   * Получить данные ROAS (Return on Ad Spend) по кампаниям
   * Теперь возвращает кривые ROAS из Supabase
   */
  static async getRoasData(filters, maxDays, minCohortSize, includeOrganic = true, options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters)
    };
    if (maxDays !== undefined) {
      params.maxDays = maxDays.toString();
    }
    if (minCohortSize !== undefined) {
      params.minCohortSize = minCohortSize.toString();
    }
    // Дефолт true не отправляем — кэш-ключи существующих запросов не меняются
    if (includeOrganic === false) {
      params.includeOrganic = 'false';
    }
    return this.cachedGet('/roas', params, options);
  }

  /**
   * Получить сводные метрики ROAS
   */
  static async getRoasSummary(filters, includeOrganic = true, options = {}) {
    const params = FiltersService.toQueryParams(filters);
    if (includeOrganic === false) {
      params.includeOrganic = 'false';
    }
    return this.cachedGet('/roas/summary', params, options);
  }

  /**
   * Получить перцентильную аналитику сессий
   * @param {Object} filters - Фильтры дашборда
   * @param {number} minLevel - Когортный гейт: только устройства с max(уровень) >= minLevel (0 = без фильтра)
   */
  static async getSessionsAnalytics(filters, minLevel = 0, options = {}) {
    const params = FiltersService.toQueryParams(filters);
    // minLevel — локальный параметр страницы. 0 = без фильтра, не отправляем
    // (бэкенд трактует отсутствие как 0).
    if (minLevel > 0) {
      params.minLevel = String(minLevel);
    }
    return this.cachedGet('/sessions/analytics', params, options);
  }
  
  /**
   * Получить прогрессию: уровень по дню жизни игрока (4 кривые).
   * Диапазон дат трактуется как когорта (acc_reg_ts).
   * @param {Object} filters - Фильтры дашборда
   */
  static async getProgressionLevelByDay(filters, options = {}) {
    const params = FiltersService.toQueryParams(filters);
    return this.cachedGet('/progression/level-by-day', params, options);
  }

  /**
   * Получить прогрессию: уровень прокачки башен по номеру кампании.
   * Диапазон дат трактуется как когорта (acc_reg_ts).
   * @param {Object} filters - Фильтры дашборда
   */
  static async getProgressionTowerByCampaign(filters, options = {}) {
    const params = FiltersService.toQueryParams(filters);
    return this.cachedGet('/progression/tower-by-campaign', params, options);
  }

  /**
   * Воронка старта (блок 1): [установка] → регистрация → уровни/туториал.
   * Диапазон дат трактуется как когорта (acc_reg_ts).
   * @param {Object} filters - Фильтры дашборда
   * @param {number|null} horizon - Горизонт наблюдения в днях; null = всё время
   * @param {string} [clientVerI] - Версия установки (client_ver_i)
   */
  static async getStartFunnel(filters, horizon = 30, clientVerI, options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      horizon: horizon === null ? 'all' : String(horizon)
    };
    if (clientVerI) params.clientVerI = clientVerI;
    return this.cachedGet('/progression/start-funnel/funnel', params, options);
  }

  /**
   * Воронка старта (блок 2): кумулятивная кривая отвала по уровням до 150.
   * @param {Object} filters - Фильтры дашборда
   * @param {number|null} horizon - Горизонт наблюдения в днях; null = всё время
   * @param {string} [clientVerI] - Версия установки (client_ver_i)
   */
  static async getStartFunnelDropoff(filters, horizon = 30, clientVerI, options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      horizon: horizon === null ? 'all' : String(horizon)
    };
    if (clientVerI) params.clientVerI = clientVerI;
    return this.cachedGet('/progression/start-funnel/dropoff', params, options);
  }

  /**
   * Воронка старта (блок 3): время до шага (медиана/среднее + доля дошедших).
   * @param {Object} filters - Фильтры дашборда
   * @param {number|null} horizon - Горизонт наблюдения в днях; null = всё время
   * @param {string} [clientVerI] - Версия установки (client_ver_i)
   */
  static async getStartFunnelTimeToStep(filters, horizon = 30, clientVerI, options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      horizon: horizon === null ? 'all' : String(horizon)
    };
    if (clientVerI) params.clientVerI = clientVerI;
    return this.cachedGet('/progression/start-funnel/time-to-step', params, options);
  }

  /**
   * Воронка старта (блок 4): сравнение до 3 когорт, дельты в п.п.
   * @param {Object} filters - Фильтры дашборда
   * @param {Array<{label?: string, startDate?: string, endDate?: string, clientVerI?: string}>} cohorts - Когорты
   * @param {number|null} horizon - Горизонт наблюдения в днях; null = всё время
   */
  static async getStartFunnelCompare(filters, cohorts = [], horizon = 30, options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      horizon: horizon === null ? 'all' : String(horizon),
      cohorts: JSON.stringify(cohorts)
    };
    return this.cachedGet('/progression/start-funnel/compare', params, options);
  }

  /**
   * Значения client_ver_i внутри когортного окна — для селектора сравнения.
   * @param {Object} filters - Фильтры дашборда
   * @param {number|null} horizon - Горизонт наблюдения в днях; null = всё время
   */
  static async getStartFunnelClientVersions(filters, horizon = 30, options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      horizon: horizon === null ? 'all' : String(horizon)
    };
    return this.cachedGet('/progression/start-funnel/client-versions', params, options);
  }

  /**
   * Воронки уровней: «широкий» набор данных по режиму (campaign/elite/absorption/td) —
   * все горизонты, все уровни, инсталлы, а при mode='absorption' ещё и тайминг
   * по всем N бустеров. Горизонт, диапазон уровней, галку «от инсталлов» и
   * N бустеров клиент режет локально (см. levelFunnel/slice.js) — сеть дёргаем
   * только при смене режима или фильтров, попадающих в WHERE таблицы событий.
   *
   * В query-string уходит НЕ весь toQueryParams(filters), а только то, что
   * реально фильтрует таблицу событий (dateRange, platform, версии — см.
   * server/levelFunnelService.ts:relevantFilters). Проект/кампании/источник
   * таблица не поддерживает, и их присутствие в URL раньше давало холодный
   * промах клиентского cachedGet при смене кампании, хотя на данные это не
   * влияло.
   * @param {Object} filters - Фильтры дашборда
   * @param {'campaign'|'elite'|'absorption'|'td'} mode - Режим воронки
   */
  static async getLevelFunnelDataset(filters, mode, options = {}) {
    const allParams = FiltersService.toQueryParams(filters);
    const RELEVANT_KEYS = ['startDate', 'endDate', 'platform', 'clientVersions', 'installVersions'];
    const query = { mode };
    RELEVANT_KEYS.forEach((key) => {
      if (allParams[key] !== undefined) query[key] = allParams[key];
    });
    return this.cachedGet('/progression/level-funnel/dataset', query, options);
  }

  /**
   * Винрейт уровня кампании по дням события (вкладка «Винрейт по дням»).
   * Диапазон дат фильтрует event_ts; в запрос идут только фильтры, которые
   * попадают в WHERE таблицы событий (dateRange, platform, версии).
   * @param {Object} filters - Фильтры дашборда
   * @param {number} level - Номер уровня кампании (1..2000)
   */
  static async getLevelWinrateByDay(filters, level, options = {}) {
    const allParams = FiltersService.toQueryParams(filters);
    const RELEVANT_KEYS = ['startDate', 'endDate', 'platform', 'clientVersions', 'installVersions'];
    const query = { level: String(level) };
    RELEVANT_KEYS.forEach((key) => {
      if (allParams[key] !== undefined) query[key] = allParams[key];
    });
    return this.cachedGet('/progression/level-winrate/by-day', query, options);
  }

  /**
   * Топ инапов (SKU) по выручке за период.
   * Диапазон дат — по дате СОБЫТИЯ (event_ts), а не по когорте.
   * @param {Object} filters - Фильтры дашборда
   * @param {number} limit - Сколько SKU вернуть
   */
  static async getTopIapItems(filters, limit = 20, options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      limit: String(limit)
    };
    return this.cachedGet('/iap/top-items', params, options);
  }

  /**
   * Разрез покупок инапов по оси: уровень кампании (acc_progress) или лайфтайм.
   * Вместе с покупателями отдаёт знаменатель (активные игроки в корзине оси),
   * чтобы считать конверсию «из какого числа людей покупают».
   * @param {Object} filters - Фильтры дашборда
   * @param {'progress'|'lifetime'} dimension - Ось разреза
   * @param {number} bucket - Размер корзины по оси
   * @param {number} limit - Сколько SKU вернуть
   */
  static async getIapBreakdown(filters, dimension = 'progress', bucket = 10, limit = 20, options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      dimension,
      bucket: String(bucket),
      limit: String(limit)
    };
    return this.cachedGet('/iap/breakdown', params, options);
  }

  /**
   * Получить данные ROAS по периодам с разбивкой по источникам
   * @param {Object} filters - Фильтры дашборда
   * @param {string} groupBy - Группировка (day/week/month)
   * @param {number} dayFromInstall - День от установки (0, 1, 3, 7, 14, 30, 60, 90, 120, 150, 180, 360)
   * @param {boolean} splitByCampaign - Разбивать ли по кампаниям
   */
  static async getRoasByPeriod(filters, groupBy = 'day', dayFromInstall = 7, splitByCampaign = false, includeOrganic = true, options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      groupBy,
      dayFromInstall: dayFromInstall.toString(),
      splitByCampaign: splitByCampaign.toString()
    };
    if (includeOrganic === false) {
      params.includeOrganic = 'false';
    }
    return this.cachedGet('/roas/by-period', params, options);
  }

  /**
   * Получить сводные данные ROAS по месяцам
   * Возвращает ВСЕ данные из базы без ограничений по датам
   */
  static async getRoasMonthlySummary(filters, includeOrganic = true, options = {}) {
    // Передаем только проект, без ограничений по датам
    const params = {
      project: filters.project
      // НЕ передаем dateRange - получаем все данные из базы
    };
    if (includeOrganic === false) {
      params.includeOrganic = 'false';
    }
    return this.cachedGet('/metrics/monthly-roas', params, options);
  }
  
  /**
   * Получить DAU/LDAU по периодам
   * @param {Object} filters - Фильтры дашборда
   * @param {string} groupBy - Группировка (day/week/month)
   */
  static async getDauByPeriod(filters, groupBy = 'day', options = {}) {
    const params = {
      ...FiltersService.toQueryParams(filters),
      groupBy
    };
    return this.cachedGet('/metrics/dau-by-period', params, options);
  }
  
  /**
   * Получить список ресурсов (p1str события resource) для селектора виджета
   * «Движение ресурсов». Когорта — по acc_reg_ts, не по дате события.
   * @param {Object} params - { dateFrom, dateTo } (обязательные, 'YYYY-MM-DD')
   */
  static async getResourceNames({ dateFrom, dateTo, clientVersions, installVersions } = {}, options = {}) {
    return this.cachedGet('/resources/names', { dateFrom, dateTo, clientVersions, installVersions }, options);
  }

  /**
   * Получить движение ресурса по оси (уровень кампании / день жизни):
   * баланс, приход или расход с квантилями p25/p50/p75 по плательщикам
   * и неплательщикам. Когорта — по acc_reg_ts.
   * @param {Object} params - { resource, dateFrom, dateTo, axis, metric,
   *   includeCheaters, platform, geo, maxX, minPlayers }; undefined поля не отправляются
   */
  static async getResourceFlow(params = {}, options = {}) {
    return this.cachedGet('/resources/flow', params, options);
  }

  /**
   * Получить все метрики активных пользователей (DAU, WAU, MAU и их L-версии)
   * @param {Object} filters - Фильтры дашборда
   * @param {string} groupBy - Группировка (day/week/month)
   * @param {Object} options - { signal, force } — прокидывается в cachedGet
   * @param {Object} metricOptions - { includeLoyal } — false убирает
   *   loyalActiveUsers из ответа (ARPDAU его не показывает) и, так как
   *   includeLoyal попадает в params, а cachedGet строит ключ кэша из URL,
   *   разводит кэш ARPDAU и DAU/MAU виджетов на один и тот же эндпоинт.
   */
  static async getActiveUsers(filters, groupBy = 'day', options = {}, metricOptions = {}) {
    const { includeLoyal } = metricOptions;
    const params = {
      ...FiltersService.toQueryParams(filters),
      groupBy,
      ...(includeLoyal === false ? { includeLoyal: false } : {})
    };
    return this.cachedGet('/metrics/active-users', params, options);
  }
  
  /**
   * Форматирование чисел
   */
  static formatNumber(value) {
    if (value === null || value === undefined) return '0';
    
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    
    return value.toLocaleString();
  }
  
  /**
   * Форматирование валюты
   */
  static formatCurrency(value, currency = 'USD') {
    if (value === null || value === undefined) return '$0';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  }
  
  /**
   * Форматирование процентов
   */
  static formatPercent(value, decimals = 1) {
    if (value === null || value === undefined) return '0%';
    
    return `${value.toFixed(decimals)}%`;
  }
  
  /**
   * Расчет изменения в процентах
   */
  static calculatePercentChange(current, previous) {
    if (!previous || previous === 0) return 0;
    
    return ((current - previous) / previous) * 100;
  }
  
  /**
   * Расчет ROI
   */
  static calculateROI(revenue, cost) {
    if (!cost || cost === 0) return 0;
    
    return ((revenue - cost) / cost) * 100;
  }
  
  /**
   * Расчет CPI (Cost Per Install)
   */
  static calculateCPI(cost, installs) {
    if (!installs || installs === 0) return 0;
    
    return cost / installs;
  }
  
  /**
   * Расчет конверсии
   */
  static calculateConversion(conversions, total) {
    if (!total || total === 0) return 0;
    
    return (conversions / total) * 100;
  }

  /**
   * Округлить значение до 2 знаков после запятой
   */
  static roundValue(value) {
    if (value === null || value === undefined) return 0;
    return Math.round(value * 100) / 100;
  }
  
  /**
   * Подставляет серверные NET-поля вместо gross (`<поле>Net` -> `<поле>`).
   * Ничего не умножает: NET считает сервер. Нет NET-поля — остаётся gross.
   * Реклама (admon) NET-полей не имеет и не меняется.
   *
   * @param {Object|Array} data - Строка или массив строк с NET-полями рядом с gross
   * @param {boolean} shouldApply - Включён ли чекбокс «чистый доход»
   * @returns {Object|Array} - Данные, где показаны NET-значения
   */
  static applyNetRevenue(data, shouldApply) {
    if (!shouldApply || !data) {
      return data;
    }

    if (Array.isArray(data)) {
      return data.map(item => this.applyNetRevenueToItem(item));
    }

    return this.applyNetRevenueToItem(data);
  }

  /**
   * Одна строка: верхний уровень и вложенный `revenue` ({inapps, inappsNet, admon}).
   */
  static applyNetRevenueToItem(item) {
    if (!item || typeof item !== 'object') {
      return item;
    }

    const result = swapNetFields(item);
    if (result.revenue && typeof result.revenue === 'object') {
      result.revenue = swapNetFields(result.revenue);
    }
    return result;
  }
}

// Экспортируем удобные функции
export const getCampaigns = (filters, options) => MetricsService.getCampaigns(filters, options);
export const getRegistrations = (filters, groupBy, options) => MetricsService.getRegistrations(filters, groupBy, options);
export const getSummary = (filters, options) => MetricsService.getSummary(filters, options);
export const formatNumber = (value) => MetricsService.formatNumber(value);
export const formatCurrency = (value) => MetricsService.formatCurrency(value);
export const formatPercent = (value) => MetricsService.formatPercent(value);
